import React, { useState, useEffect, useRef, createContext } from "react";
import { HashRouter as Router, Route, Redirect, Link, useLocation } from "react-router-dom";
import {
  getData,
  updateDBData,
  resetDBData,
  exportDBData,
  importDBData,
  addProblemToTopic,
  addTopic as addTopicDB,
  deleteProblem as deleteProblemDB,
  deleteTopic as deleteTopicDB,
  getApplyData,
  updateApplyDataDB,
} from "./services/dbServices";
import { slugify } from "./utils/slug";
import { saveAs } from "file-saver";
import Spinner from "react-bootstrap/Spinner";
import TopicCard from "./components/TopicCard/TopicCard";
import Topic from "./components/Topic/Topic";
import Dashboard from "./components/Dashboard/Dashboard";
import Practice from "./components/Practice/Practice";
import Revise from "./components/Revise/Revise";
import Mock from "./components/Mock/Mock";
import About from "./components/About/About";
import Apply from "./components/Apply/Apply";
import Admin from "./components/Admin/Admin";
import Sidebar from "./components/Sidebar/Sidebar";
import Analytics from "./components/Analytics";
import FeedbackButton from "./components/Feedback/FeedbackButton";
import LofiWidget from "./components/Music/LofiWidget";
import { FiMenu } from "react-icons/fi";
import ReactGA from "react-ga4";
import { AuthProvider, useAuth } from "./auth/AuthContext";
import { apiGet, apiPut } from "./auth/api";
import { cloudMerge } from "./utils/cloudMerge";
import { applyMerge } from "./utils/applyMerge";
import AuthModal from "./components/Auth/AuthModal";
import "./App.css";

// Creating a theme context
export const ThemeContext = createContext(null);

function AppInner() {
  const { token, email, isAuthed, logout } = useAuth();

  // setting state for data received from the DB
  const [questionData, setquestionData] = useState([]);

  // per-company "Apply" tracking (applied / cold-DM / notes / hidden-forever /
  // user-added companies)
  const [applyData, setApplyData] = useState({ blocklist: [], tracked: {}, custom: [] });

  // if dark theme is enabled or not — dark is the default for new visitors
  const [dark, setDark] = useState(true);

  // mobile sidebar open/closed
  const [navOpen, setNavOpen] = useState(false);

  // auth modal + cloud-sync status
  const [authOpen, setAuthOpen] = useState(false);
  const [cloudReady, setCloudReady] = useState(false);
  const [syncState, setSyncState] = useState("idle"); // idle | syncing | synced | error

  // latest questionData, without making the pull effect re-run on every edit
  const questionDataRef = useRef(questionData);
  useEffect(() => {
    questionDataRef.current = questionData;
  }, [questionData]);

  // latest applyData, for the same reason (read inside the login-pull effect)
  const applyDataRef = useRef(applyData);
  useEffect(() => {
    applyDataRef.current = applyData;
  }, [applyData]);

  // last payload we actually uploaded — lets us skip redundant cloud writes
  const lastUploadRef = useRef("");

  // useEffect for fetching data from DB on load and init GA
  useEffect(() => {
    // Analytics is opt-in: only initialises if you provide your own tracking id.
    if (process.env.REACT_APP_GA_TRACKING_ID) {
      ReactGA.initialize(process.env.REACT_APP_GA_TRACKING_ID);
    }
    getData((QuestionData) => {
      setquestionData(QuestionData);
    });
    getApplyData((apply) => {
      setApplyData(apply);
    });

    //implementing dark theme mode option
    // checking if dark mode "isDark" is already declared or not
    if (!("isDark" in window.localStorage)) {
      window.localStorage.setItem("isDark", dark);
    } else {
      // initialising the value of dark with the already stored value
      let temp = window.localStorage["isDark"];
      if (temp === "false") {
        setDark(false);
      } else {
        setDark(true);
      }
    }
      // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // On sign-in: pull the cloud copy, union it with this device's data,
  // persist locally and adopt it. cloudReady gates the upload effect so we
  // never push local-only state up before the cloud copy has been merged in.
  useEffect(() => {
    if (!isAuthed) {
      setCloudReady(false);
      setSyncState("idle");
      return;
    }
    let cancelled = false;
    setSyncState("syncing");
    apiGet("/data", token)
      .then((r) => {
        if (cancelled) return;
        const cloud = r && r.questionData;
        if (cloud && cloud.length) {
          const merged = cloudMerge(questionDataRef.current, cloud);
          importData(merged, () => {});
        }
        const cloudApply = r && r.applyData;
        if (cloudApply) {
          const mergedApply = applyMerge(applyDataRef.current, cloudApply);
          setApplyData(mergedApply);
          updateApplyDataDB(mergedApply);
        }
        setCloudReady(true);
      })
      .catch(() => {
        if (!cancelled) {
          setSyncState("error");
          setCloudReady(true);
        }
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthed, token]);

  // Push to the cloud (debounced) whenever data changes while signed in.
  // We compare against the last uploaded snapshot and skip the network call
  // entirely when nothing actually changed, and use a slightly longer debounce
  // so a burst of edits collapses into a single write.
  useEffect(() => {
    if (!isAuthed || !cloudReady) return;
    if (!questionData || questionData.length === 0) return;
    const payload = JSON.stringify({ questionData, applyData });
    if (payload === lastUploadRef.current) {
      setSyncState("synced");
      return; // identical to what's already in the cloud — no call needed
    }
    setSyncState("syncing");
    const id = setTimeout(() => {
      apiPut("/data", { questionData, applyData }, token)
        .then(() => {
          lastUploadRef.current = payload;
          setSyncState("synced");
        })
        .catch(() => setSyncState("error"));
    }, 2000);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questionData, applyData, cloudReady, isAuthed, token]);

  // Sign out keeps the local cache intact (logged-out still works offline).
  function handleSignOut() {
    logout();
    setCloudReady(false);
    setSyncState("idle");
  }

  //to update progress in '/' route and also update DB
  function updateData(key, topicData, topicPosition) {
    let reGenerateUpdatedData = questionData.map((topic, index) => {
      if (index === topicPosition) {
        updateDBData(key, topicData);
        return { topicName: topic.topicName, position: topic.position, Custom: topic.Custom, ...topicData };
      } else {
        return topic;
      }
    });
    setquestionData(reGenerateUpdatedData);
  }

  // Update the Apply (jobs) tracking state: adopt locally + persist to IndexedDB.
  // The debounced cloud-push effect picks up the applyData change automatically.
  function updateApply(next) {
    setApplyData(next);
    updateApplyDataDB(next);
  }

  // add a custom problem to a topic
  function addProblem(slug, problem, callback) {
    addProblemToTopic(slug, problem, (data) => {
      setquestionData(data);
      callback && callback();
    });
  }

  // add a brand new custom topic
  function addTopic(name, callback) {
    addTopicDB(name, (res) => {
      if (res && res.ok) {
        getData((data) => setquestionData(data));
      }
      callback && callback(res);
    });
  }

  // delete a custom problem
  function deleteProblem(slug, index, callback) {
    deleteProblemDB(slug, index, (data) => {
      setquestionData(data);
      callback && callback();
    });
  }

  // delete a custom topic
  function deleteTopic(slug, callback) {
    deleteTopicDB(slug, (data) => {
      setquestionData(data);
      callback && callback();
    });
  }

  // reset and clear DB
  function resetData() {
    resetDBData((response) => {
      setquestionData([]);
      window.location.replace(window.location.origin);
    });
  }

  // export 450DSA-Progress data
  function exportData(callback) {
    exportDBData((data) => {
      const fileData = JSON.stringify(data, null, 2);
      const blob = new Blob([fileData], { type: "text/plain" });
      saveAs(blob, "progress.json");
      callback();
    });
  }

  // import 450DSA-Progress data
  function importData(data, callback) {
    importDBData(data, (QuestionData) => {
      setquestionData(QuestionData);
      callback();
    });
  }

  // find the topic index for a given slug (generic, index-independent routing)
  function findTopicIndex(slug) {
    return questionData.findIndex((topic) => slugify(topic.topicName) === slug);
  }

  return (
    <Router>
      <div className={`app-shell ${dark ? "dark" : ""} ${navOpen ? "nav-open" : ""}`}>
        <Analytics />
        {questionData.length === 0 ? (
          <div className="nb-loading">
            <Spinner animation="border" variant="secondary" />
            <div>Loading your workspace…</div>
          </div>
        ) : (
          <ThemeContext.Provider value={dark}>
            <div className="nav-overlay" onClick={() => setNavOpen(false)} />
            <Sidebar
              questionData={questionData}
              dark={dark}
              setDark={setDark}
              onNavigate={() => setNavOpen(false)}
              isAuthed={isAuthed}
              email={email}
              syncState={syncState}
              onSignIn={() => setAuthOpen(true)}
              onSignOut={handleSignOut}
            />
            <div className="main">
              <TopBar questionData={questionData} onMenu={() => setNavOpen(true)} />
              <div className="main-inner">
                {/* HOME */}
                <Route
                  exact
                  path="/"
                  children={
                    <TopicCard
                      questionData={questionData}
                      addTopic={addTopic}
                      deleteTopic={deleteTopic}
                      addProblem={addProblem}
                    />
                  }
                />

                {/* DASHBOARD */}
                <Route
                  path="/dashboard"
                  children={
                    <Dashboard
                      questionData={questionData}
                      updateData={updateData}
                      applyData={applyData}
                    />
                  }
                />

                {/* PRACTICE — random problem picker with filters + timer */}
                <Route
                  path="/practice"
                  children={<Practice questionData={questionData} updateData={updateData} />}
                />

                {/* REVISE — spaced-repetition review queue */}
                <Route
                  path="/revise"
                  children={<Revise questionData={questionData} updateData={updateData} />}
                />

                {/* MOCK — timed interview session + lofi background music */}
                <Route
                  path="/mock"
                  children={<Mock questionData={questionData} updateData={updateData} />}
                />

                {/* APPLY — companies to apply to (random + A–Z) with tracking */}
                <Route
                  path="/apply"
                  children={<Apply applyData={applyData} updateApply={updateApply} />}
                />

                {/* ABOUT */}
                <Route
                  path="/about"
                  children={
                    <About
                      resetData={resetData}
                      exportData={exportData}
                      importData={importData}
                      setQuestionData={setquestionData}
                    />
                  }
                />

                {/* ADMIN — hidden owner-only dashboard (gated server-side by ADMIN_EMAIL) */}
                <Route path="/admin" children={<Admin />} />
                {/* GENERIC TOPIC ROUTE — works for every seed and custom topic */}
                <Route
                  path="/topic/:slug"
                  render={({ match }) => {
                    const idx = findTopicIndex(match.params.slug);
                    if (idx === -1) return <Redirect to="/" />;
                    return (
                      <Topic
                        data={questionData[idx]}
                        updateData={updateData}
                        addProblem={addProblem}
                        deleteProblem={deleteProblem}
                      />
                    );
                  }}
                />
              </div>
            </div>
          </ThemeContext.Provider>
        )}
        <AuthModal show={authOpen} onHide={() => setAuthOpen(false)} />
        <LofiWidget />
        <FeedbackButton />
      </div>
    </Router>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppInner />
    </AuthProvider>
  );
}

// Breadcrumb bar rendered at the top of the main column (inside the Router so
// it can read the active route). Also hosts the mobile menu button.
function TopBar({ questionData, onMenu }) {
  const location = useLocation();
  const path = location.pathname;
  let crumb = null;
  if (path.startsWith("/dashboard")) crumb = "Dashboard";
  else if (path.startsWith("/practice")) crumb = "Practice";
  else if (path.startsWith("/apply")) crumb = "Apply";
  else if (path.startsWith("/about")) crumb = "About";
  else if (path.startsWith("/topic/")) {
    const slug = path.split("/topic/")[1];
    const t = questionData.find((x) => slugify(x.topicName) === slug);
    crumb = t ? t.topicName : "Topic";
  }
  return (
    <div className="topbar">
      <button type="button" className="menu-btn" onClick={onMenu} aria-label="Open menu">
        <FiMenu size={18} />
      </button>
      {crumb ? (
        <>
          <Link to="/" className="crumb">
            Home
          </Link>
          <span className="crumb-sep">/</span>
          <span className="crumb-current">{crumb}</span>
        </>
      ) : (
        <span className="crumb-current">Home</span>
      )}
    </div>
  );
}

export default App;
